import { ApiResult } from '@synergy/api';
import { Media } from '@synergy/models';
import { PaginatedResponse } from '@synergy/api';
import { request, safeRequest } from '@synergy/api';

import { Offer } from '../models/offer';
import { SupplierWithContact } from '../../features/inquiries/models/supplier-with-contact';

/** The shop's price for a shop article of a delivery address. */
export interface ShopPrice {
    purchaseItemId: number;
    price: number;
}

export class OfferApi {
    constructor(protected baseUrl: string) {}

    public createOffers(
        inquiryId: number,
        supplierAccounts: SupplierWithContact[]
    ): Promise<ApiResult<PaginatedResponse<Offer>>> {
        return safeRequest(
            () => request.post(`${this.baseUrl}/inquiries/${inquiryId}/offers`, { supplierAccounts }),
            response => {
                response.data.items = response.data.items.map(Offer.fromPlainObject);
                return response.data;
            }
        );
    }

    public getShopPrices(accountPurchaseId: number, supplierAccountId: number): Promise<ApiResult<ShopPrice[]>> {
        return safeRequest(
            () =>
                request.get(`${this.baseUrl}/account-purchases/${accountPurchaseId}/shop-prices`, {
                    params: { supplierAccountId },
                }),
            response => response.data as ShopPrice[]
        );
    }

    public addOffer(accountPurchaseId: number, offer: Partial<Offer>): Promise<ApiResult<Offer>> {
        return safeRequest(
            () => request.post(`${this.baseUrl}/account-purchases/${accountPurchaseId}/offers`, offer),
            response => Offer.fromPlainObject(response.data)
        );
    }

    public updateOffer(offer: Partial<Offer>): Promise<ApiResult<Offer>> {
        return safeRequest(
            () => request.patch(`${this.baseUrl}/offers/${offer.id}`, offer),
            response => Offer.fromPlainObject(response.data)
        );
    }

    public declineOffer(offerId: number, emailSubject: string, emailText: string): Promise<ApiResult<Offer>> {
        return safeRequest(
            () => request.post(`${this.baseUrl}/offers/${offerId}/decline`, { emailSubject, emailText }),
            response => Offer.fromPlainObject(response.data)
        );
    }

    public recommendOffer(offerId: number, isRecommended: boolean): Promise<ApiResult<Offer>> {
        return safeRequest(
            () => request.post(`${this.baseUrl}/offers/${offerId}/recommend`, { isRecommended }),
            response => Offer.fromPlainObject(response.data)
        );
    }

    public sendPriceComparison(
        accountPurchaseId: number,
        supplierAccountIds: number[],
        allDeliveryAddresses = false,
        supplierAccountIdsByAddress?: Record<number, number[]>
    ): Promise<ApiResult<Media>> {
        return safeRequest<Media, Media[]>(
            () =>
                request.post(`${this.baseUrl}/account-purchases/${accountPurchaseId}/mail-price-matrix`, {
                    supplierAccountIds,
                    allDeliveryAddresses,
                    supplierAccountIdsByAddress,
                }),
            response => Media.fromPlainObject(response.data[0])
        );
    }
}
